/**
 * Caja: el dinero de verdad, que no es lo mismo que la ganancia.
 *
 * CODIGO PURO. No conoce la base de datos.
 *
 * EL PROBLEMA QUE ESTA PARTE EXISTE PARA RESOLVER
 *
 * La ganancia se genera al vender. El dinero llega cuando el cliente paga, y sale
 * cuando se compran insumos. Los tres momentos son distintos, y con clientes que
 * pagan cada semana o cada quince dias la diferencia se vuelve enorme.
 *
 * Sus propios numeros al 4 de septiembre lo dicen:
 *
 *   cobrado                     $6,400.00
 *   comprado en insumos        -$9,034.52
 *   ---------------------------------------
 *   dinero que puso de su bolsa  $2,634.52
 *
 * Ese dinero no se perdio: $2,400 estan por cobrar y $5,452 en la despensa. El
 * negocio va bien y aun asi ella tuvo que poner dinero. Sin esta vista, eso solo
 * se descubre cuando la cartera esta vacia.
 *
 * LAS TRES COSAS QUE NUNCA SE DEBEN SUMAR
 *
 *   dinero en caja    lo que puede gastar hoy
 *   por cobrar        lo que le deben por comida YA ENTREGADA
 *   inventario        lo que ya pago y esta en la despensa
 *
 * Son valor del negocio las tres, pero solo la primera se puede gastar. Por eso
 * `dondeEstaElDinero` las devuelve separadas y nunca colapsadas en un solo total
 * sin etiqueta.
 *
 * Y HAY UNA CUARTA QUE NO ES VALOR TODAVIA
 *
 *   apalabrado        pedidos que aun no entrega
 *
 * Se informa, pero fuera del total. Nadie le debe por comida que no ha entregado,
 * y los ingredientes de esos pedidos ya estan contados en la despensa.
 *
 * LO QUE ESTA PARTE NO TOCA
 *
 * La formula de la ganancia no cambia: sigue siendo ventas menos costo de la
 * comida, y una venta pendiente de cobro sigue contando como ganancia desde que se
 * registra. Las cuentas por cobrar tampoco cambian. Esto se suma al lado, no
 * reemplaza nada.
 *
 * EL DINERO DE ELLA NO ES INGRESO DEL NEGOCIO
 *
 * Una aportacion suya sube el saldo de la caja, pero NO es dinero que el negocio
 * gano. Por eso el resumen reporta dos flujos distintos: el del negocio, que no
 * cuenta su dinero, y el movimiento real de la caja, que si. Mezclarlos haria ver
 * un mes rentable solo porque ella metio dinero.
 */

import { ErrorValidacion } from './errors';
import type { Centavos } from './money';
import type { FechaLocal } from './dates';

// ---------------------------------------------------------------------------
// Tipos de movimiento
// ---------------------------------------------------------------------------

/**
 * Los movimientos de dinero que NO se pueden deducir de otra tabla.
 *
 * Los cobros salen de los pedidos pagados y las compras de los tickets, asi que no
 * se capturan aqui: se leerian dos veces y podrian discrepar. Aqui solo vive lo que
 * hoy no existe en ninguna parte.
 */
export type TipoMovimientoCaja =
  /** Ella mete dinero de su cartera para que el negocio siga operando. */
  | 'aportacion'
  /** El negocio le regresa dinero que ella habia puesto. */
  | 'devolucion'
  /** Ella saca ganancia para si misma. No es gasto del negocio. */
  | 'retiro'
  /** Gasto que no es insumo: gas, bolsas, desechables, transporte. */
  | 'gasto'
  /** Dinero que entra y no es una venta. Poco comun, pero pasa. */
  | 'otro_ingreso';

export const TIPOS_MOVIMIENTO_CAJA: readonly TipoMovimientoCaja[] = [
  'gasto',
  'aportacion',
  'retiro',
  'devolucion',
  'otro_ingreso',
];

export const ETIQUETA_MOVIMIENTO_CAJA: Readonly<Record<TipoMovimientoCaja, string>> = {
  gasto: 'Gasto del negocio',
  aportacion: 'Dinero que puse yo',
  retiro: 'Dinero que saqué para mí',
  devolucion: 'El negocio me regresó dinero',
  otro_ingreso: 'Otro dinero que entró',
};

/** Explicacion corta para el formulario, para no tener que adivinar cual es cual. */
export const AYUDA_MOVIMIENTO_CAJA: Readonly<Record<TipoMovimientoCaja, string>> = {
  gasto: 'Gas, bolsas, desechables, transporte. Todo lo que pagas y no es un insumo.',
  aportacion: 'Cuando pones dinero de tu cartera porque la caja no alcanza.',
  retiro: 'Cuando tomas dinero del negocio para ti. No es un gasto del negocio.',
  devolucion: 'Cuando el negocio te devuelve dinero que tú habías puesto.',
  otro_ingreso: 'Dinero que entra y no viene de una venta.',
};

/**
 * Categorias sugeridas de gasto. Es una lista abierta: se puede escribir otra.
 *
 * Sirve para poder responder "en que se me va el dinero" sin obligar a encasillar
 * cada gasto en una lista cerrada que siempre queda corta.
 */
export const CATEGORIAS_GASTO: readonly string[] = [
  'Gas',
  'Bolsas y desechables',
  'Transporte',
  'Limpieza',
  'Servicios',
  'Herramientas',
  'Otro',
];

/** +1 si el dinero entra a la caja, -1 si sale. */
export function signoDe(tipo: TipoMovimientoCaja): 1 | -1 {
  return tipo === 'aportacion' || tipo === 'otro_ingreso' ? 1 : -1;
}

/** true para los movimientos que son dinero DE ELLA, no del negocio. */
export function esDineroDeElla(tipo: TipoMovimientoCaja): boolean {
  return tipo === 'aportacion' || tipo === 'devolucion' || tipo === 'retiro';
}

// ---------------------------------------------------------------------------
// El movimiento
// ---------------------------------------------------------------------------

/** La forma minima que necesitan los calculos. La tabla guarda algo mas. */
export interface MovimientoParaCaja {
  fecha: FechaLocal;
  tipo: TipoMovimientoCaja;
  /** SIEMPRE positivo. El signo lo decide el tipo. */
  montoCentavos: Centavos;
}

/**
 * El monto se guarda positivo y el tipo decide el signo.
 *
 * Guardar montos negativos invita al error de capturar un gasto en positivo y que
 * sume en lugar de restar. Con esta regla, un gasto es un gasto aunque se capture
 * mal el signo.
 */
export function exigirMontoDeCaja(monto: number): Centavos {
  if (!Number.isFinite(monto) || !Number.isInteger(monto)) {
    throw new ErrorValidacion('El monto debe ser una cantidad entera de centavos.');
  }
  if (monto <= 0) {
    throw new ErrorValidacion('El monto tiene que ser mayor que cero.');
  }
  return monto;
}

// ---------------------------------------------------------------------------
// Resumen de un periodo
// ---------------------------------------------------------------------------

export interface EntradasDeCaja {
  /** Cobrado a clientes en el periodo. Sale de los pedidos pagados. */
  cobradoCentavos: Centavos;
  /** Comprado en insumos en el periodo. Sale de los tickets de compra. */
  comprasCentavos: Centavos;
  /** Los movimientos capturados a mano que caen en el periodo. */
  movimientos: readonly MovimientoParaCaja[];
  /**
   * Ganancia de la comida del periodo, tal como la calcula el motor de ganancias.
   * Entra como dato para poder restarle los gastos, NO se recalcula aqui.
   */
  gananciaDeLaComidaCentavos: Centavos | null;
}

export interface ResumenDeCaja {
  // --- Lo que entro
  cobradoCentavos: Centavos;
  otrosIngresosCentavos: Centavos;
  aportacionesCentavos: Centavos;

  // --- Lo que salio
  comprasCentavos: Centavos;
  gastosCentavos: Centavos;
  retirosCentavos: Centavos;
  devolucionesCentavos: Centavos;

  /**
   * Efectivo que genero el NEGOCIO, sin contar el dinero de ella.
   *
   * Es el numero que dice si el negocio se sostiene solo. Negativo significa que
   * esa semana el negocio necesito dinero de fuera.
   */
  flujoDelNegocioCentavos: Centavos;

  /** El movimiento real del saldo, contando tambien el dinero de ella. */
  movimientoNetoCentavos: Centavos;

  /**
   * Ganancia de la comida menos los gastos que no son insumos.
   *
   * La pantalla de Ganancias no incluye gas ni bolsas, porque su formula solo
   * conoce el costo de los insumos. Este numero si los resta, y es el que
   * contesta "cuanto gano el negocio de verdad".
   *
   * null cuando la ganancia no esta disponible, para no fingir un dato.
   */
  utilidadDelNegocioCentavos: Centavos | null;

  /** Gasto agrupado por categoria, de mayor a menor. */
  gastosPorCategoria: Array<{ categoria: string; montoCentavos: Centavos }>;
}

/** Igual que MovimientoParaCaja pero con la categoria, para poder agrupar. */
export interface MovimientoConCategoria extends MovimientoParaCaja {
  categoria?: string | null;
}

export function resumenDeCaja(
  datos: EntradasDeCaja & { movimientos: readonly MovimientoConCategoria[] },
): ResumenDeCaja {
  const suma = (tipo: TipoMovimientoCaja): Centavos =>
    datos.movimientos
      .filter((m) => m.tipo === tipo)
      .reduce((s, m) => s + m.montoCentavos, 0);

  const aportacionesCentavos = suma('aportacion');
  const otrosIngresosCentavos = suma('otro_ingreso');
  const gastosCentavos = suma('gasto');
  const retirosCentavos = suma('retiro');
  const devolucionesCentavos = suma('devolucion');

  const flujoDelNegocioCentavos =
    datos.cobradoCentavos + otrosIngresosCentavos - datos.comprasCentavos - gastosCentavos;

  const movimientoNetoCentavos =
    flujoDelNegocioCentavos + aportacionesCentavos - retirosCentavos - devolucionesCentavos;

  const porCategoria = new Map<string, Centavos>();
  for (const m of datos.movimientos) {
    if (m.tipo !== 'gasto') continue;
    const clave = m.categoria === null || m.categoria === undefined || m.categoria === '' ? 'Otro' : m.categoria;
    porCategoria.set(clave, (porCategoria.get(clave) ?? 0) + m.montoCentavos);
  }

  return {
    cobradoCentavos: datos.cobradoCentavos,
    otrosIngresosCentavos,
    aportacionesCentavos,
    comprasCentavos: datos.comprasCentavos,
    gastosCentavos,
    retirosCentavos,
    devolucionesCentavos,
    flujoDelNegocioCentavos,
    movimientoNetoCentavos,
    utilidadDelNegocioCentavos:
      datos.gananciaDeLaComidaCentavos === null
        ? null
        : datos.gananciaDeLaComidaCentavos - gastosCentavos,
    gastosPorCategoria: [...porCategoria.entries()]
      .map(([categoria, montoCentavos]) => ({ categoria, montoCentavos }))
      .sort((a, b) => b.montoCentavos - a.montoCentavos || a.categoria.localeCompare(b.categoria, 'es')),
  };
}

// ---------------------------------------------------------------------------
// Saldo acumulado
// ---------------------------------------------------------------------------

export interface SaldoDeCaja {
  /** Dinero que puede gastar hoy. Puede ser negativo si falto capturar algo. */
  saldoCentavos: Centavos;
  /** Dinero de ella que sigue dentro del negocio: puesto menos devuelto. */
  aportacionNetaCentavos: Centavos;
  /** Todo lo que ha sacado para si misma. */
  retiradoCentavos: Centavos;
}

/**
 * Saldo de la caja a una fecha, sumando TODA la historia hasta ahi.
 *
 * A diferencia del resumen, esto no es de un periodo: un saldo es acumulado por
 * naturaleza. El dinero que quedo la semana pasada sigue ahi esta semana.
 */
export function saldoDeCaja(datos: {
  cobradoCentavos: Centavos;
  comprasCentavos: Centavos;
  movimientos: readonly MovimientoParaCaja[];
}): SaldoDeCaja {
  const suma = (tipo: TipoMovimientoCaja): Centavos =>
    datos.movimientos
      .filter((m) => m.tipo === tipo)
      .reduce((s, m) => s + m.montoCentavos, 0);

  const aportaciones = suma('aportacion');
  const devoluciones = suma('devolucion');
  const retiros = suma('retiro');

  const saldoCentavos =
    datos.cobradoCentavos +
    suma('otro_ingreso') +
    aportaciones -
    datos.comprasCentavos -
    suma('gasto') -
    retiros -
    devoluciones;

  return {
    saldoCentavos,
    aportacionNetaCentavos: aportaciones - devoluciones,
    retiradoCentavos: retiros,
  };
}

// ---------------------------------------------------------------------------
// Donde esta el dinero
// ---------------------------------------------------------------------------

export interface DondeEstaElDinero {
  /** Lo unico que se puede gastar hoy. */
  enCajaCentavos: Centavos;

  /**
   * Comida que ya entrego y que no le han pagado. Esto SI se lo deben.
   *
   * Es la unica parte del por cobrar que cuenta como valor del negocio: la comida
   * ya salio de su cocina, el cliente se la llevo y el cobro es exigible.
   */
  entregadoSinPagarCentavos: Centavos;

  /**
   * Pedidos apalabrados que todavia no entrega.
   *
   * A PROPOSITO NO ENTRA EN EL TOTAL. Nadie le debe por comida que no ha
   * entregado, y varios de esos pedidos ni se han cocinado: sus ingredientes
   * siguen contados en la despensa. Sumarlo seria contar la misma comida dos
   * veces, una como insumo y otra como venta, e inflaria el valor del negocio
   * con dinero que aun no se gana.
   *
   * Se reporta aparte porque si sirve saberlo: es lo que viene en camino.
   */
  apalabradoCentavos: Centavos;

  /**
   * Los dos de arriba juntos.
   *
   * Existe para poder cuadrar con la pantalla de Cobrar, que muestra el por cobrar
   * completo sin partirlo. Que las dos pantallas se puedan reconciliar importa
   * mas que ahorrarse un campo.
   */
  porCobrarCentavos: Centavos;

  /** Ya se pago y esta en la despensa. Tampoco se puede gastar. */
  enInventarioCentavos: Centavos;

  /**
   * Caja + entregado sin pagar + despensa. Es el valor del negocio, NO su efectivo.
   *
   * No incluye lo apalabrado, por el motivo explicado arriba.
   */
  totalCentavos: Centavos;

  /** De ese total, cuanto es dinero de ella que sigue metido. */
  aportacionNetaCentavos: Centavos;
  /** El resto: lo que el negocio genero y todavia trae dentro. */
  delNegocioCentavos: Centavos;

  /**
   * Cuanto puede comprar hoy sin sacar dinero de su cartera.
   *
   * Es el saldo de la caja, ni un peso mas. Se nombra aparte porque es la
   * pregunta con la que llega a la tienda, y confundirla con el total es
   * exactamente el error que la deja poniendo dinero.
   */
  puedeComprarCentavos: Centavos;
}

/**
 * Reparte el valor del negocio en los tres lugares donde puede estar.
 *
 * El total NUNCA se presenta solo: se presenta con sus tres partes, porque tener
 * $7,852 repartidos entre calle y despensa no es lo mismo que tenerlos en la mano,
 * y esa confusion es justo la que hay que evitar.
 *
 * POR QUE EL POR COBRAR LLEGA PARTIDO EN DOS
 *
 * Ella lo pregunto y al medirlo salio esto: de $2,400 por cobrar, solo $1,020 eran
 * de comida ya entregada. Los otros $1,380 eran pedidos de los dias siguientes,
 * varios sin cocinar. Contarlos como valor del negocio decia que tenia mas de lo
 * que tenia, y encima los ingredientes de esos pedidos ya estaban contados en la
 * despensa: la misma comida dos veces.
 */
export function dondeEstaElDinero(datos: {
  saldo: SaldoDeCaja;
  /** Ya entregado y sin pagar. Cuenta en el total. */
  entregadoSinPagarCentavos: Centavos;
  /** Apalabrado y sin entregar. NO cuenta en el total. */
  apalabradoCentavos: Centavos;
  enInventarioCentavos: Centavos;
}): DondeEstaElDinero {
  const enCajaCentavos = datos.saldo.saldoCentavos;
  const totalCentavos =
    enCajaCentavos + datos.entregadoSinPagarCentavos + datos.enInventarioCentavos;

  return {
    enCajaCentavos,
    entregadoSinPagarCentavos: datos.entregadoSinPagarCentavos,
    apalabradoCentavos: datos.apalabradoCentavos,
    porCobrarCentavos: datos.entregadoSinPagarCentavos + datos.apalabradoCentavos,
    enInventarioCentavos: datos.enInventarioCentavos,
    totalCentavos,
    aportacionNetaCentavos: datos.saldo.aportacionNetaCentavos,
    delNegocioCentavos: totalCentavos - datos.saldo.aportacionNetaCentavos,
    // Nunca se sugiere gastar de mas: si la caja esta en rojo, la capacidad es cero.
    puedeComprarCentavos: Math.max(0, enCajaCentavos),
  };
}
